import { useEffect, useCallback, useId, useRef, RefObject } from 'react';
import {
  registerCardVideo,
  unregisterCardVideo,
  isPriorityVideoActive,
} from '@/utils/media/videoPauseCoordinator';
import isElementVisible from '@/utils/dom/isElementVisible';
import safeTryPlayVideo from '@/utils/media/safeTryPlayVideo';

type UseCardVideoOptions = {
  videoRef: RefObject<HTMLVideoElement>;
  isVideo?: boolean;
  shouldLoadVideo: boolean;
};

const useCardVideo = ({
  videoRef,
  isVideo,
  shouldLoadVideo,
}: UseCardVideoOptions) => {
  const shouldPlayRef = useRef(false);

  const videoId = useId();

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isVideo || !shouldLoadVideo) return;

    const tryPlay = () => {
      if (isPriorityVideoActive()) return;
      safeTryPlayVideo(video);
    };

    const pauseVideo = () => video.pause();
    const resumeVideo = () => {
      if (shouldPlayRef.current && !isPriorityVideoActive()) tryPlay();
    };

    registerCardVideo({ id: videoId, pause: pauseVideo, resume: resumeVideo });

    const observer = new IntersectionObserver(
      ([entry]) => {
        shouldPlayRef.current = entry.isIntersecting;
        if (entry.isIntersecting) tryPlay();
        else video.pause();
      },
      { threshold: 0.5 }
    );

    observer.observe(video);

    const initialPlayTimeout = setTimeout(() => {
      if (isElementVisible(video)) {
        shouldPlayRef.current = true;
        tryPlay();
      }
    }, 100);

    return () => {
      clearTimeout(initialPlayTimeout);
      observer.disconnect();
      unregisterCardVideo(videoId);
      video.pause();
      shouldPlayRef.current = false;
    };
  }, [videoRef, isVideo, shouldLoadVideo, videoId]);

  const handleLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (video && shouldPlayRef.current && !isPriorityVideoActive()) {
      safeTryPlayVideo(video);
    }
  }, [videoRef]);

  const handleCanPlay = useCallback(
    (onReady: () => void) => {
      onReady();
      const video = videoRef.current;
      if (video && shouldPlayRef.current && !isPriorityVideoActive()) {
        safeTryPlayVideo(video);
      }
    },
    [videoRef]
  );

  return { shouldPlayRef, handleLoadedMetadata, handleCanPlay };
};

export default useCardVideo;
